import React, { useState, useEffect, useRef } from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CircularProgress } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";

import ViewGstr1Report from "./ViewGstr1Report";
import { useAuth } from "../../../context/AuthProvider";
import { Link } from "react-router-dom";
import { ChevronRightIcon, DownloadIcon, SpreadSheetIcon } from "../../../constants/Icons";

const Gstr1Report = () => {
  const { isUser } = useAuth();
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const serverTime = GetServerLiveTime();

  var obj = {
    label: isUser.title,
    value: isUser.storeId,
    stype: isUser.stype,
  };

  const [isPreLoading, setPreLoading] = useState(true);
  const [isbtnLoading, setButtonLoading] = useState(false);
  const [isSelectedOption, setSelectedOption] = useState("CONSOLIDATED");
  const [isGstr1ReportData, setGstr1ReportData] = useState([]);
  const [options, setOptions] = useState([]);
  const [isSelectedStore, setSelectedStore] = useState(obj);

  const [error, setErrors] = useState("");
  const modeSelectedRef = useRef(null);
  const storeSelectedRef = useRef(null);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const currentDate = new Date(); // today
  const previousDate = new Date(serverTime.getTime() - 1000);

  const [fromDate, setFromDate] = useState(previousDate);
  const [toDate, setToDate] = useState(serverTime);

  const calculateDate = (baseDate, offsetDays) => {
    const resultDate = new Date(baseDate);
    resultDate.setDate(resultDate.getDate() + offsetDays);
    return resultDate;
  };

  const handleFromDateChange = (newValue) => {
    if (!newValue) return;
    const selectedFrom = newValue.startOf("day");

    const maxAllowedTo = selectedFrom.add(64, "days");
    const finalMaxTo = maxAllowedTo.isAfter(dayjs(serverTime))
      ? dayjs(serverTime)
      : maxAllowedTo;

    setFromDate(selectedFrom.toDate());
    if (
      dayjs(toDate).isBefore(selectedFrom) ||
      dayjs(toDate).isAfter(finalMaxTo)
    ) {
      setToDate(finalMaxTo.toDate());
    }
  };

  const handleToDateChange = (newValue) => {
    if (!newValue) return;
    const selectedTo = newValue.endOf("day");

    if (selectedTo.isBefore(dayjs(fromDate))) {
      setFromDate(selectedTo.startOf("day").toDate());
    }
    setToDate(selectedTo.toDate());
  };

  const toDateMin = fromDate;
  const toDateMax = dayjs(fromDate).add(64, "day").isAfter(dayjs(serverTime))
    ? dayjs(serverTime)
    : dayjs(fromDate).add(64, "day");

  const handleSelectOption = (event) => {
    const selected = event.target.value;
    setSelectedOption(selected);
  };

  const handleSelectStore = (selected) => {
    setSelectedStore(selected);
  };

  const handleListStore = async () => {
    setPreLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`list-store`);
      const options = formatOptions(response.data.results, isUser.stype);
      setOptions(options);
      const userStore = options.find(
        (option) => option.value === isUser.storeId
      );
      setSelectedStore(userStore);
      handleGstr1Report();
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
    }
  };

  const formatOptions = (data, userType) => {
    if (userType === "MAIN") {
      const mainOption = {
        label: data.title,
        value: data._id,
        stype: "MAIN",
      };
      const subOptions = data.subs.map((subStore) => ({
        label: subStore.title,
        value: subStore._id,
        stype: "SUB",
      }));
      const hubOptions = data.subs.reduce((acc, subStore) => {
        if (subStore.hubs && subStore.hubs.length > 0) {
          const hubs = subStore.hubs.map((hub) => ({
            label: hub.title,
            value: hub._id,
            stype: "HUB",
          }));
          return acc.concat(hubs);
        }
        return acc;
      }, []);
      return [mainOption, ...subOptions, ...hubOptions];
    }

    if (userType === "SUB") {
      const subOption = {
        label: data.title,
        value: data._id,
        stype: "SUB",
      };
      let hubOptions = [];
      if (data.hubs && data.hubs.length > 0) {
        hubOptions = data.hubs.map((hub) => ({
          label: hub.title,
          value: hub._id,
          stype: "HUB",
        }));
      }

      return [subOption, ...hubOptions];
    }
    if (userType == "HUB") {
      const hubOptions = {
        label: data.title,
        value: data._id,
        stype: "HUB",
      };
      return [hubOptions];
    }
    return [];
  };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "main":
        return "bg-label-orange";
      case "sub":
        return "bg-label-peach";
      case "hub":
        return "bg-label-night";
      default:
        return "bg-label-secondary";
    }
  };

  const renderOptionLabel = ({ label, stype }) => {
    let marginRight;
    let fontWeight;
    let badgePadding;
    let fontSize;

    if (stype === "MAIN") {
      marginRight = "0px";
      fontWeight = "500";
      fontSize = "14.5px";
      badgePadding = "2px 8px";
    } else if (stype === "SUB") {
      marginRight = "2px";
      fontWeight = "400";
      fontSize = "14px";
      badgePadding = "2px 12px";
    } else if (stype === "HUB") {
      marginRight = "4px";
      fontWeight = "300";
      fontSize = "13.5px";
      badgePadding = "2px 14px";
    }

    return (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          marginLeft: marginRight,
        }}
      >
        <span
          className={`badge rounded-pill ${getColor(stype)}`}
          style={{
            marginRight: "5px",
            padding: badgePadding,
          }}
        >
          {stype}
        </span>
        <span style={{ fontWeight, fontSize }}>- {label}</span>
      </span>
    );
  };

  const validateForm = () => {
    const errors = {};
    if (!isSelectedOption) {
      errors.mode = Messages.ERROR.FIELD_REQUIRED;
    } else if (!isSelectedStore) {
      errors.store = Messages.ERROR.EMPTY_STORE;
    }

    setErrors(errors);
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      switch (firstErrorField) {
        case "mode":
          modeSelectedRef.current?.focus();
          break;
        case "store":
          storeSelectedRef.current?.focus();
          break;
        default:
          break;
      }
    }
    return Object.keys(errors).length === 0;
  };

  const handleGstr1Report = async () => {
    if (!validateForm()) return;
    setPreLoading(true);
    setButtonLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = {
        storeid: isSelectedStore.value,
        mode: isSelectedOption,
        fromdate: encodeURIComponent(
          DateConverter.convertToTimeZone(
            fromDate.toISOString(),
            isUser.timezone
          )
        ),
        todate: encodeURIComponent(
          DateConverter.convertToTimeZone(toDate.toISOString(), isUser.timezone)
        ),
      };

      const response = await AxiosInstance.get(
        `module/reports/gstr1?fromdate=${data.fromdate}&todate=${data.todate}&reportType=${data.mode}`
      );

      setGstr1ReportData(response.data.results);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPreLoading(false);
      setButtonLoading(false);
    }
  };

  const handleReset = () => {
    var obj = {
      label: isUser.title,
      value: isUser.storeId,
      stype: isUser.stype,
    };
    setSelectedOption("CONSOLIDATED");
    setSelectedStore(obj);
  };

  useEffect(() => {
    handleListStore();
  }, []);

  const exportToCSV = () => {
    if (!isGstr1ReportData?.reportData?.length) {
      showAlert("warning", "No data available to export");
      return;
    }

    const isSeparated =
      isGstr1ReportData?.reportInfo.reportType === "SEPARATED";
    const isConsolidated =
      isGstr1ReportData?.reportInfo.reportType === "CONSOLIDATED";

    const headers =
      `Invoice No,Invoice Date,Mode,Customer Name,State,State Code,GSTIN,` +
      (isSeparated ? "Product Title," : "") +
      `Base Price,GST %,CGST Amount,SGST Amount,IGST Amount,Total Tax,Total Amount\n`;

    const clean = (val) => (val ?? "").toString().replace(/[\r\n;]/g, " ");

    const rows = isGstr1ReportData?.reportData
      .map((item) => {
        const row = [
          clean(item.saleno),
          clean(DateConverter.ISOToDisplayFormat(item.saledate).dateSlash),
          clean(item.saletype),
          clean(item.customername),
          clean(item.state),
          clean(item.statecode),
          clean(item.gstnumber),
        ];

        if (isSeparated) {
          row.push(clean(item.title));
        }

        row.push(
          clean(
            CurrencyConverter.toCurrency(item.lineTotalBasePrice || 0, "INR")
          ),
          clean(item.taxpercentage),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalCgstAmount || 0, "INR")
          ),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalSgstAmount || 0, "INR")
          ),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalIgstAmount || 0, "INR")
          ),
          clean(
            CurrencyConverter.toCurrency(item.lineTotalTaxAmount || 0, "INR")
          ),
          clean(CurrencyConverter.toCurrency(item.lineGrandTotal || 0, "INR"))
        );

        return row.join(",");
      })
      .join("\n");

    const summary = isGstr1ReportData?.reportSummary || {};
    const totalRowLabel = isSeparated ? "TOTALS,,,,,,," : "TOTALS,,,,,,";
    const totalsRow = [
      totalRowLabel,
      clean(CurrencyConverter.toCurrency(summary?.totalBasePrice || 0, "INR")),
      "", // GST %
      clean(CurrencyConverter.toCurrency(summary?.totalCgstAmount || 0, "INR")),
      clean(CurrencyConverter.toCurrency(summary?.totalSgstAmount || 0, "INR")),
      clean(CurrencyConverter.toCurrency(summary?.totalIgstAmount || 0, "INR")),
      clean(CurrencyConverter.toCurrency(summary?.totalTaxAmount || 0, "INR")),
      clean(CurrencyConverter.toCurrency(summary?.grandTotal || 0, "INR")),
    ].join(",");
    const summaryHeader =
      "\n\nTax %,Tax Amount,SGST Amount,CGST Amount,IGST Amount";

    const taxSummary = isGstr1ReportData?.taxSummary || [];

    // Rows with taxpercentage > 0
    const summaryRows = taxSummary
      .filter((item) => Number(item?.taxpercentage) > 0)
      .map((item) => {
        return [
          `${item.taxpercentage}%`,
          CurrencyConverter.toCurrency(
            Number(item?.totalTaxAmount) || 0,
            "INR"
          ),
          CurrencyConverter.toCurrency(
            Number(item?.totalSgstAmount) || 0,
            "INR"
          ),
          CurrencyConverter.toCurrency(
            Number(item?.totalCgstAmount) || 0,
            "INR"
          ),
          CurrencyConverter.toCurrency(
            Number(item?.totalIgstAmount) || 0,
            "INR"
          ),
        ].join(",");
      });

    // ✅ Totals row from the last array item
    let totalRow = "";
    if (taxSummary.length > 0) {
      const totals = taxSummary[taxSummary.length - 1];
      totalRow = [
        "Total",
        CurrencyConverter.toCurrency(totals?.taxTotal || 0, "INR"),
        CurrencyConverter.toCurrency(totals?.sgstTotal || 0, "INR"),
        CurrencyConverter.toCurrency(totals?.cgstTotal || 0, "INR"),
        CurrencyConverter.toCurrency(totals?.igstTotal || 0, "INR"),
      ].join(",");
    }

    const finalSummary = [summaryHeader, ...summaryRows, totalRow]
      .filter(Boolean) // remove empty strings
      .join("\n");

    // const summaryBlock = summaryHeader + "\n" + finalSummary;
    const csvContent = headers + rows + "\n" + totalsRow + finalSummary;
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const from = formatDate(fromDate);
    const to = formatDate(toDate);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `GSTR1_Report_${from}_to_${to}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // const downloadExcel = () => {
  //     const table = document.querySelector(".rtTable");
  //     if (!table) {
  //         return;
  //     }
  //     const tableHTML = table.outerHTML.replace(/ /g, "%20");
  //     const dataUri = `data:application/vnd.ms-excel;charset=utf-8,${tableHTML}`;
  //     const link = document.createElement("a");
  //     link.href = dataUri;
  //     link.download = "Gstr1_Report.xls";
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     ;
  // }

  return (
    <div>
      <div className="page-wrapper">
        <div className="page-content">
          <div className="page-breadcrumb d-flex align-items-center paddingBottom">
            <div className="breadcrumb-title pe-3">Gstr1 Report</div>
            <div className="ms-auto">
              <div className="dropdown">
                <button
                  className="btn btn-sm btn-export dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <DownloadIcon/> Export
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <Link className="dropdown-item" onClick={exportToCSV}>
                      <SpreadSheetIcon/>{" "}
                      <span className="icon-text">Excel (CSV)</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    <div className="col-lg-2">
                      <label className="form-label w-100">
                        From Date <span className="text-danger"> *</span>
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dayjs(fromDate)}
                          maxDate={dayjs(serverTime)}
                          format="DD MMM YYYY"
                          onChange={handleFromDateChange}
                          slotProps={{
                            textField: {
                              className: "form-control w-200",
                              size: "small",
                              inputProps: { readOnly: true },
                              onKeyDown: (e) => e.preventDefault(),
                              sx: {
                                "& .MuiInputBase-input": {
                                  cursor: "pointer", 
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="col-lg-2">
                      <label className="form-label w-100">
                        To Date <span className="text-danger"> *</span>
                      </label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dayjs(toDate)}
                          minDate={dayjs(toDateMin)}
                          maxDate={dayjs(toDateMax)}
                          format="DD MMM YYYY"
                          onChange={handleToDateChange}
                          slotProps={{
                            textField: {
                              className: "form-control w-200",
                              size: "small",
                              inputProps: { readOnly: true },
                              onKeyDown: (e) => e.preventDefault(),
                              sx: {
                                "& .MuiInputBase-input": {
                                  cursor: "pointer", 
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                    <div className="col-lg-2">
                      <label className="form-label w-100">
                        Mode <span className="text-danger"> *</span>
                      </label>
                      <select
                        className="form-select w-200"
                        onChange={handleSelectOption}
                        ref={modeSelectedRef}
                        value={isSelectedOption}
                      >
                        <option value="">Select</option>
                        <option value="DETAILED">Detailed</option>
                        <option value="CONSOLIDATED">Consolidated</option>
                      </select>
                      {error.mode && (
                        <div className="invalid-feedback">{error.mode}</div>
                      )}
                    </div>

                    {/* <div className="col-lg-3">
                      <label className="form-label w-100">
                        Store <span className="text-danger"> *</span>
                      </label>
                      <Select
                        options={options}
                        onChange={handleSelectStore}
                        value={isSelectedStore}
                        ref={storeSelectedRef}
                        isSearchable
                        placeholder="Select Store"
                        formatOptionLabel={renderOptionLabel}
                      />
                      {error.store && (
                        <div className="invalid-feedback">{error.store}</div>
                      )}
                    </div> */}

                    <div className="col-lg-2 float-end">
                      <label className="form-label w-100"> &nbsp;</label>
                      {isbtnLoading ? (
                        <button className="btn btn-primary pb-0" disabled>
                          <CircularProgress size={22} className="text-white" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          id="gstr1reportbtn"
                          onClick={handleGstr1Report}
                        >
                          Generate <ChevronRightIcon/>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-12">
              <div className="card p-3">
                <ViewGstr1Report
                  isData={isGstr1ReportData}
                  isPreLoading={isPreLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gstr1Report;